import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  Alert,
  Box,
  Tabs,
  Tab
} from '@mui/material';
import ChecklistEditor from './ChecklistEditor';

const ServiceCatalogForm = ({ 
  open, 
  onClose, 
  onSubmit, 
  service = null, 
  tenantId, 
  cityCode,
  loading = false,
  error = null 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    icon: '',
    description: '',
    priceModel: '',
    unit: '',
    rate: '',
    currency: 'USD',
    seasonStart: '',
    seasonEnd: '',
    active: true,
    checklist: []
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [activeTab, setActiveTab] = useState(0);

  // Service types - could be moved to config/constants
  const serviceTypes = [
    'CLEANING',
    'DEEP_CLEANING', 
    'MOVE_IN_OUT',
    'OFFICE_CLEANING',
    'WINDOW_CLEANING',
    'CARPET_CLEANING',
    'PRESSURE_WASHING',
    'LAUNDRY',
    'ORGANIZING',
    'OTHER'
  ];

  // Price models
  const priceModels = [
    'FLAT_RATE',
    'PER_HOUR',
    'PER_SQFT',
    'PER_ROOM',
    'PER_BATHROOM',
    'PER_BEDROOM',
    'CUSTOM'
  ];

  // Units corresponding to price models
  const units = [
    'SERVICE',
    'HOUR',
    'SQFT',
    'ROOM',
    'BATHROOM',
    'BEDROOM',
    'ITEM'
  ];

  // Initialize form data when service prop changes
  useEffect(() => {
    if (service) {
      setFormData({
        name: service.name || '',
        type: service.type || '',
        icon: service.icon || '',
        description: service.description || '',
        priceModel: service.priceModel || '',
        unit: service.unit || '',
        rate: service.rate || '',
        currency: service.currency || 'USD',
        seasonStart: service.seasonStart ? service.seasonStart.split('T')[0] : '',
        seasonEnd: service.seasonEnd ? service.seasonEnd.split('T')[0] : '',
        active: service.active !== undefined ? service.active : true,
        checklist: service.checklist || []
      });
    } else {
      // Reset form for new service
      setFormData({
        name: '',
        type: '',
        icon: '',
        description: '',
        priceModel: '',
        unit: '',
        rate: '',
        currency: 'USD',
        seasonStart: '',
        seasonEnd: '',
        active: true,
        checklist: []
      });
    }
    setValidationErrors({});
  }, [service, open]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    const requiredFields = ['name', 'type', 'priceModel', 'unit', 'rate', 'seasonStart', 'seasonEnd'];
    
    requiredFields.forEach(field => {
      if (!formData[field]) {
        errors[field] = `${field.charAt(0).toUpperCase() + field.slice(1)} is required`;
      }
    });

    // Validate rate is positive number
    if (formData.rate && parseFloat(formData.rate) <= 0) {
      errors.rate = 'Rate must be greater than 0';
    }

    // Validate date range
    if (formData.seasonStart && formData.seasonEnd) {
      const startDate = new Date(formData.seasonStart);
      const endDate = new Date(formData.seasonEnd);
      if (startDate >= endDate) {
        errors.seasonEnd = 'End date must be after start date';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    const submitData = {
      ...formData,
      rate: parseFloat(formData.rate),
      tenantId,
      cityCode
    };

    onSubmit(submitData);
  };

  const handleChecklistChange = (newChecklist) => {
    setFormData(prev => ({
      ...prev,
      checklist: newChecklist
    }));
  };

  const isEditing = !!service;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {isEditing ? 'Edit Service' : 'Create New Service'}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 2 }}>
          <Tab label="Basic Information" />
          <Tab label="Task Checklist" />
        </Tabs>

        {activeTab === 0 && (
          <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Service Name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              error={!!validationErrors.name}
              helperText={validationErrors.name}
              required
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!validationErrors.type}>
              <InputLabel required>Service Type</InputLabel>
              <Select
                value={formData.type}
                onChange={(e) => handleInputChange('type', e.target.value)}
                label="Service Type"
              >
                {serviceTypes.map(type => (
                  <MenuItem key={type} value={type}>
                    {type.replace('_', ' ')}
                  </MenuItem>
                ))}
              </Select>
              {validationErrors.type && (
                <Box sx={{ fontSize: '0.75rem', color: 'error.main', mt: 0.5, ml: 1.5 }}>
                  {validationErrors.type}
                </Box>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Icon (optional)"
              value={formData.icon}
              onChange={(e) => handleInputChange('icon', e.target.value)}
              placeholder="e.g., cleaning, deep-clean, window"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description (optional)"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!validationErrors.priceModel}>
              <InputLabel required>Price Model</InputLabel>
              <Select
                value={formData.priceModel}
                onChange={(e) => handleInputChange('priceModel', e.target.value)}
                label="Price Model"
              >
                {priceModels.map(model => (
                  <MenuItem key={model} value={model}>
                    {model.replace('_', ' ')}
                  </MenuItem>
                ))}
              </Select>
              {validationErrors.priceModel && (
                <Box sx={{ fontSize: '0.75rem', color: 'error.main', mt: 0.5, ml: 1.5 }}>
                  {validationErrors.priceModel}
                </Box>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!validationErrors.unit}>
              <InputLabel required>Unit</InputLabel>
              <Select
                value={formData.unit}
                onChange={(e) => handleInputChange('unit', e.target.value)}
                label="Unit"
              >
                {units.map(unit => (
                  <MenuItem key={unit} value={unit}>
                    {unit}
                  </MenuItem>
                ))}
              </Select>
              {validationErrors.unit && (
                <Box sx={{ fontSize: '0.75rem', color: 'error.main', mt: 0.5, ml: 1.5 }}>
                  {validationErrors.unit}
                </Box>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Rate"
              value={formData.rate}
              onChange={(e) => handleInputChange('rate', e.target.value)}
              error={!!validationErrors.rate}
              helperText={validationErrors.rate}
              inputProps={{ min: 0, step: 0.01 }}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Currency</InputLabel>
              <Select
                value={formData.currency}
                onChange={(e) => handleInputChange('currency', e.target.value)}
                label="Currency"
              >
                <MenuItem value="USD">USD</MenuItem>
                <MenuItem value="EUR">EUR</MenuItem>
                <MenuItem value="GBP">GBP</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="Season Start"
              value={formData.seasonStart}
              onChange={(e) => handleInputChange('seasonStart', e.target.value)}
              error={!!validationErrors.seasonStart}
              helperText={validationErrors.seasonStart}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="Season End"
              value={formData.seasonEnd}
              onChange={(e) => handleInputChange('seasonEnd', e.target.value)}
              error={!!validationErrors.seasonEnd}
              helperText={validationErrors.seasonEnd}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.active}
                  onChange={(e) => handleInputChange('active', e.target.checked)}
                />
              }
              label="Active"
            />
          </Grid>
          </Grid>
        )}

        {activeTab === 1 && (
          <Box sx={{ mt: 1 }}>
            <ChecklistEditor
              checklist={formData.checklist}
              onChange={handleChecklistChange}
              disabled={loading}
            />
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading}
        >
          {loading ? 'Saving...' : (isEditing ? 'Update' : 'Create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ServiceCatalogForm;